/**
 * Dicts FR centralisés pour les enums backend (raw `win/loss/draw`,
 * `lichess/chesscom`, `syzygy/muller_lamprecht/...`, modules `opening/tactic/
 * endgame/autopsy`).
 *
 * But : aucune chaîne enum brute ne doit fuiter à l'UI affichée à Léon
 * (11 ans, single-user FR). Les noms TypeScript/types/identifiants restent
 * en anglais — seuls les labels rendus changent.
 *
 * Convention : si la clé manque, le caller utilise `?? key` comme fallback
 * de sécurité (ne casse pas l'écran si un nouveau code arrive du backend).
 *
 * Voir aussi `app/src/ui/modals.tsx` (moduleLabels), `panels.tsx` (modules
 * dots), `GameListPage.tsx`/`EndgameListPage.tsx` (result/source/source_kind).
 */

export const MODULE_LABEL_FR: Record<string, string> = {
  opening: 'Ouverture',
  tactic: 'Tactiques',
  endgame: 'Finale',
  autopsy: 'Autopsie',
};

export const GAME_RESULT_LABEL_FR: Record<string, string> = {
  win: 'Victoire',
  loss: 'Défaite',
  draw: 'Nulle',
};

export const GAME_SOURCE_LABEL_FR: Record<string, string> = {
  lichess: 'Lichess',
  chesscom: 'chess.com',
};

export const ENDGAME_SOURCE_KIND_LABEL_FR: Record<string, string> = {
  syzygy: 'Tablebase Syzygy',
  muller_lamprecht: 'Müller-Lamprecht',
  stockfish: 'Stockfish',
};

/**
 * Labels FR des thèmes de tactique servis par
 * `GET /api/v1/content/tactics/themes`. Le backend renvoie les slugs raw
 * Lichess (snake_case ou camelCase selon la convention upstream). Cette
 * source unique est consommée par TacticsListPage, TacticsDrillPage et
 * ParentDashboardPage — toute nouvelle surface qui affiche un thème doit
 * passer par ce dico (`TACTICS_THEME_LABEL_FR[slug] ?? slug`) pour ne pas
 * faire fuiter de slug dev-EN à Léon.
 *
 * Glossaire FR retenu (sources : FFE Cavalier, ChessTempo FR) :
 *  - `mate_in_N`        → « Mat forcé » (catégorie agrégée Lichess)
 *  - `intermediate_move`→ « Coup intermédiaire » (équivalent Zwischenzug)
 *  - `interference`     → « Interception »
 *  - `x_ray`            → « Rayon X »
 *  - `removing_defender`→ « Élimination du défenseur »
 *  - `overloading`      → « Surcharge »
 *  - `discovered_attack`→ « Attaque découverte »
 *  - `double_attack`    → « Double attaque »
 *  - `deflection`       → « Déviation »
 *  - `decoy`            → « Attraction »
 *  - `pin`              → « Clouage »
 *  - `skewer`           → « Enfilade »
 *  - `zugzwang`         → « Zugzwang » (terme international conservé)
 *
 * Entrées héritées pour rétro-compat data (puzzles importés avant
 * normalisation des slugs Lichess) : `fork`, `mate`, `mateIn1/2/3`.
 */
export const TACTICS_THEME_LABEL_FR: Record<string, string> = {
  deflection: 'Déviation',
  decoy: 'Attraction',
  discovered_attack: 'Attaque découverte',
  double_attack: 'Double attaque',
  pin: 'Clouage',
  skewer: 'Enfilade',
  x_ray: 'Rayon X',
  zugzwang: 'Zugzwang',
  removing_defender: 'Élimination du défenseur',
  overloading: 'Surcharge',
  interference: 'Interception',
  intermediate_move: 'Coup intermédiaire',
  mate_in_N: 'Mat forcé',
  // Rétro-compat data legacy (avant normalisation slugs Lichess MVP2.2).
  fork: 'Fourchette',
  mate: 'Mat',
  mateIn1: 'Mat en 1',
  mateIn2: 'Mat en 2',
  mateIn3: 'Mat en 3',
};
